import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Check } from 'lucide-react';

/** Normaliza texto: minúsculas + elimina tildes/diacríticos para búsqueda robusta.
 *  "García" → "garcia",  "José" → "jose",  "ÑOÑO" → "nono" */
const normalizeText = (s: string): string =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

interface DarkSelectProps<T> {
    options: T[];
    value: string | number | null;
    onChange: (value: any) => void;
    labelKey: keyof T;
    valueKey: keyof T;
    placeholder?: string;
    icon?: React.ElementType;
    disabled?: boolean;
    className?: string;
    searchable?: boolean;
    onSearch?: (query: string) => void;
    isLoading?: boolean;
}


export function DarkSelect<T extends Record<string, any>>({
    options,
    value,
    onChange,
    labelKey,
    valueKey,
    placeholder = 'Seleccionar...',
    icon: Icon,
    disabled = false,
    className = '',
    searchable = true,
    autoFocusSearch = true,
    onSearch,
    isLoading = false
}: DarkSelectProps<T> & { autoFocusSearch?: boolean }) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});

    // Find selected item object based on the primitive value prop
    const selectedItem = options.find(opt => opt[valueKey] == value);

    // Filter options based on search (accent-aware)
    const filteredOptions = onSearch ? options : options.filter(opt => {
        if (!searchTerm) return true;
        return normalizeText(String(opt[labelKey])).includes(normalizeText(searchTerm));
    });

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Si el evento viene del portal, no cerrar
                const portal = document.getElementById('select-portal-root');
                if (portal && portal.contains(event.target as Node)) return;
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Actualizar posición del portal dinámicamente al abrir y al mover la pantalla
    useEffect(() => {
        if (!isOpen || !containerRef.current) return;

        let lastRect = { top: 0, left: 0, width: 0 };

        const updatePosition = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            
            // Solo actualizar si la posición o tamaño real cambiaron
            if (
                rect.bottom !== lastRect.top - 8 || 
                rect.left !== lastRect.left || 
                rect.width !== lastRect.width
            ) {
                lastRect = { top: rect.bottom + 8, left: rect.left, width: rect.width };
                setDropdownStyles({
                    position: 'fixed',
                    top: rect.bottom + 8,
                    left: rect.left,
                    width: rect.width,
                    zIndex: 9999
                });
            }
        };

        // Actualización inicial
        updatePosition();

        // Escuchar scroll y cambios de pantalla/teclado
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true); // capture: true para capturar scroll del modal

        // Animación de refresco continuo para seguir transiciones y teclado virtual
        let animationFrameId: number;
        const loop = () => {
            updatePosition();
            animationFrameId = requestAnimationFrame(loop);
        };
        animationFrameId = requestAnimationFrame(loop);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            cancelAnimationFrame(animationFrameId);
        };
    }, [isOpen]);

    const handleSelect = (item: T) => {
        onChange(item[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
    };

    const dropdownMenu = (
        <div 
            style={dropdownStyles}
            id="select-portal-root"
            className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100"
        >
            {/* Search Input */}
            {searchable && (
                <div className="p-2 border-b border-slate-200 dark:border-dark-border">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSearchTerm(val);
                                if (onSearch) onSearch(val);
                            }}
                            placeholder={isLoading ? "Buscando..." : "Buscar..."}
                            className="w-full bg-white dark:bg-dark-input text-slate-900 dark:text-white text-base md:text-xs rounded-lg pl-8 pr-3 py-2 outline-none focus:ring-1 focus:ring-primary"
                            autoFocus={autoFocusSearch}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-red-500"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Options List */}
            <div className="max-h-64 overflow-y-auto p-1 custom-scrollbar">
                {filteredOptions.length > 0 ? (
                    filteredOptions.map((opt) => {
                        const isSelected = opt[valueKey] == value;
                        return (
                            <button
                                key={String(opt[valueKey])}
                                onClick={() => handleSelect(opt)}
                                className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between text-sm transition-colors group ${isSelected
                                    ? 'bg-primary text-white'
                                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-dark-input hover:text-slate-900 dark:hover:text-white'
                                    }`}
                            >
                                <span className="truncate flex-1 pr-2">{String(opt[labelKey])}</span>
                                {isSelected && <Check className="w-4 h-4 shrink-0" />}
                            </button>
                        );
                    })
                ) : (
                    <div className="px-4 py-6 text-center text-xs text-slate-500">
                        No se encontraron resultados
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={`w-full flex items-center justify-between bg-white dark:bg-dark-card border rounded-xl px-4 py-3 transition-all ${isOpen ? 'border-primary ring-1 ring-primary/30' : 'border-slate-200 dark:border-dark-border hover:border-primary/50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                <div className="flex items-center gap-3 overflow-hidden">
                    {Icon && (
                        <Icon className={`w-5 h-5 ${selectedItem ? 'text-primary' : 'text-slate-500'}`} />
                    )}
                    <span className={`block truncate text-sm font-medium ${selectedItem ? 'text-slate-900 dark:text-white' : 'text-slate-500'}`}>
                        {selectedItem ? String(selectedItem[labelKey]) : placeholder}
                    </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && createPortal(dropdownMenu, document.body)}
        </div>
    );
}


// Add simple CSS for custom scrollbar if needed
const style = document.createElement('style');
style.textContent = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #2D3B55;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #374151;
  }
`;
document.head.appendChild(style);
